import { withBase } from '../config/api'

let csrfToken = ''
let serverUnavailableShown = false

// Jednoduchá in-memory cache pre GET requesty
const responseCache = new Map()

const buildCacheKey = (urlOrPath, options = {}) => {
  const method = (options.method || 'GET').toUpperCase()
  return `${method}:${urlOrPath}`
}

const cloneCachedResponse = (cached) => {
  return {
    ok: cached.ok,
    status: cached.status,
    headers: {
      get: (name) => cached.headers[String(name || '').toLowerCase()] || null,
    },
    json: async () => cached.body,
    text: async () => JSON.stringify(cached.body ?? ''),
  }
}

export const invalidateFetchCache = (prefix = '') => {
  if (!prefix) {
    responseCache.clear()
    return
  }

  for (const key of responseCache.keys()) {
    if (key.includes(prefix)) {
      responseCache.delete(key)
    }
  }
}

export const fetchWithAuth = async (urlOrPath, options = {}, lang) => {
  const method = (options.method || 'GET').toUpperCase()
  const hasBody = typeof options.body !== 'undefined'
  const cacheTtlMs = typeof options.cacheTtlMs === 'number' ? options.cacheTtlMs : 0
  const forceRefresh = Boolean(options.forceRefresh)
  const cacheKey = buildCacheKey(urlOrPath, options)

  const headers = {
    ...(options.headers || {}),
    ...(hasBody && !(options.body instanceof FormData)
      ? { 'Content-Type': 'application/json' }
      : {}),
    ...(lang ? { 'Accept-Language': lang } : {}),
    ...(
      csrfToken && !['GET', 'HEAD', 'OPTIONS'].includes(method)
        ? { 'X-CSRF-Token': csrfToken }
        : {}
    ),
  }

  // Cache len pre GET/HEAD requesty
  const canUseCache = cacheTtlMs > 0 && ['GET', 'HEAD'].includes(method)
  if (canUseCache && !forceRefresh) {
    const existing = responseCache.get(cacheKey)
    if (existing && existing.expiresAt > Date.now()) {
      return cloneCachedResponse(existing)
    }
  }

  // Odstráň interné cache options, aby nešli do fetch()
  const {
    cacheTtlMs: _cacheTtlMs,
    forceRefresh: _forceRefresh,
    ...cleanOptions
  } = options

  let res

  try {
    res = await fetch(withBase(urlOrPath), {
      ...cleanOptions,
      headers,
      credentials: 'include',
    })
  } catch (err) {
    if (!serverUnavailableShown) {
      serverUnavailableShown = true
      window.dispatchEvent(new Event('server-unavailable'))
    }

    return {
      ok: false,
      status: 0,
      headers: {
        get: () => null,
      },
      json: async () => null,
      text: async () => '',
    }
  }

  const newToken = res.headers.get('x-csrf-token')
  if (newToken) {
    csrfToken = newToken
  }

  if (res.status === 401 && !urlOrPath.includes('/auth/me')) {
    window.dispatchEvent(new Event('auth-logout'))
  }

  // Ulož úspešnú GET odpoveď do cache
  if (canUseCache && res.ok) {
    try {
      const cloned = res.clone()
      const body = await cloned.json()
      const headersObj = {}
      cloned.headers.forEach((value, key) => {
        headersObj[String(key).toLowerCase()] = value
      })

      responseCache.set(cacheKey, {
        ok: res.ok,
        status: res.status,
        headers: headersObj,
        body,
        expiresAt: Date.now() + cacheTtlMs,
      })
    } catch {
      // Ignorujeme nepodariteľné parsovanie do cache
    }
  }

  return res
}